import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ganttApi } from '../../api/gantt.js';

/**
 * Task-level Gantt — interactive SVG timeline for a single project.
 *
 * Capabilities:
 *   - Horizontal bars (one per scheduled issue) with drag-to-move (whole bar)
 *     and drag-to-resize (right edge).
 *   - Dependency arrows (finish-to-start) between predecessor/successor bars.
 *   - Critical-path highlight (toggle).
 *   - Baseline overlay (ghost bars under live bars when toggled).
 *   - Today line, milestone diamonds (sprint end + version release), zoom
 *     levels (week/month/quarter), CSV export, baseline snapshot creation.
 *   - All client edits are batched into a single PUT /gantt/bulk on mouseup.
 */

const ZOOM_LEVELS = {
  week:    { label: 'Week',    daysPerCol: 1, colWidth: 40 },
  month:   { label: 'Month',   daysPerCol: 7, colWidth: 70 },
  quarter: { label: 'Quarter', daysPerCol: 30, colWidth: 90 },
};

const FALLBACK_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
const ROW_HEIGHT = 28;
const ROW_GAP = 6;
const SIDEBAR_WIDTH = 280;
const HEADER_HEIGHT = 56;
const MILESTONE_BAND = 20;

const ymd = (d) => new Date(d).toISOString().slice(0, 10);
const parseYmd = (s) => new Date(s + 'T00:00:00Z');
const addDays = (date, n) => {
  const d = new Date(date);
  d.setUTCDate(d.getUTCDate() + n);
  return d;
};
const dayDiff = (a, b) => Math.round((parseYmd(ymd(a)) - parseYmd(ymd(b))) / 86_400_000);

function shortMonth(date) {
  return date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
}
function dayLabel(date) {
  return date.toLocaleString('en-US', { weekday: 'short', timeZone: 'UTC' }).slice(0, 1);
}

export default function Timeline() {
  const { key } = useParams();
  const [data, setData] = useState(null);
  const [zoom, setZoom] = useState('week');
  const [drag, setDrag] = useState(null); // { mode, issueId, startX, originalStart, originalEnd, currentStart, currentEnd }
  const [hover, setHover] = useState(null);
  const [criticalPath, setCriticalPath] = useState(null);
  const [showCritical, setShowCritical] = useState(false);
  const [showBaseline, setShowBaseline] = useState(false);
  const [activeBaseline, setActiveBaseline] = useState(null);
  const [showDeps, setShowDeps] = useState(true);
  const [groupBy, setGroupBy] = useState('none');
  const [collapsed, setCollapsed] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const scrollerRef = useRef(null);

  const reload = useCallback(() => {
    ganttApi.getTimeline(key)
      .then((d) => {
        setData(d);
        if (d?.settings) {
          setZoom(d.settings.zoom_level || 'week');
          setShowBaseline(!!d.settings.show_baselines);
          setShowDeps(d.settings.show_dependencies !== false);
          setShowCritical(!!d.settings.show_critical_path);
          setGroupBy(d.settings.group_by || 'none');
          setCollapsed(new Set(d.settings.collapsed_groups || []));
        }
      })
      .catch(() => setData({ issues: [], unscheduled: [], dependencies: [], baselines: [], milestones: [], date_range: null, settings: {} }));
  }, [key]);

  useEffect(() => { reload(); }, [reload]);

  // Persist settings (debounced via effect dependency).
  useEffect(() => {
    if (!data) return;
    const t = setTimeout(() => {
      ganttApi.updateSettings(key, {
        zoom_level: zoom,
        show_baselines: showBaseline,
        show_dependencies: showDeps,
        show_critical_path: showCritical,
        group_by: groupBy,
        collapsed_groups: [...collapsed],
      }).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, showBaseline, showDeps, showCritical, groupBy, collapsed]);

  // Critical path on demand.
  useEffect(() => {
    if (!showCritical || !data) { setCriticalPath(null); return; }
    ganttApi.criticalPath(key).then(setCriticalPath).catch(() => setCriticalPath(null));
  }, [showCritical, key, data]);

  // Baseline snapshot on demand.
  useEffect(() => {
    if (!showBaseline || !data?.baselines?.length) { setActiveBaseline(null); return; }
    const id = data.baselines[0].id;
    ganttApi.getBaseline(key, id).then(setActiveBaseline).catch(() => setActiveBaseline(null));
  }, [showBaseline, data, key]);

  // ── Layout geometry ─────────────────────────────────────────────────
  const layout = useMemo(() => {
    if (!data) return null;
    const z = ZOOM_LEVELS[zoom] || ZOOM_LEVELS.week;
    const min = data.date_range?.min ? parseYmd(data.date_range.min) : addDays(new Date(), -14);
    const max = data.date_range?.max ? parseYmd(data.date_range.max) : addDays(new Date(), 30);
    const totalDays = Math.max(14, dayDiff(max, min));
    const cols = Math.ceil(totalDays / z.daysPerCol);
    const width = Math.max(800, cols * z.colWidth);
    return { min, max, totalDays, cols, width, colWidth: z.colWidth, daysPerCol: z.daysPerCol };
  }, [data, zoom]);

  const xForDate = useCallback((iso) => {
    if (!layout || !iso) return 0;
    return (dayDiff(iso, layout.min) / layout.daysPerCol) * layout.colWidth;
  }, [layout]);

  // ── Group + flatten rows ────────────────────────────────────────────
  const groupedRows = useMemo(() => {
    if (!data) return [];
    const issues = [...data.issues];
    if (groupBy === 'none') {
      return [{ key: '__all__', label: null, items: issues }];
    }
    const buckets = new Map();
    for (const it of issues) {
      let bk, label;
      if (groupBy === 'assignee') {
        bk = it.assignee_id || '__none__';
        label = it.assignee_name || it.assignee_email || 'Unassigned';
      } else if (groupBy === 'epic') {
        bk = it.epic_id || '__none__';
        label = it.epic_title || 'No epic';
      } else if (groupBy === 'status') {
        bk = it.status || '__none__';
        label = it.status || 'Unknown';
      } else {
        bk = '__all__'; label = null;
      }
      if (!buckets.has(bk)) buckets.set(bk, { key: bk, label, items: [] });
      buckets.get(bk).items.push(it);
    }
    return [...buckets.values()];
  }, [data, groupBy]);

  const flatRows = useMemo(() => {
    const rows = [];
    for (const g of groupedRows) {
      if (g.label !== null) rows.push({ kind: 'group', group: g });
      if (g.label === null || !collapsed.has(g.key)) {
        for (const it of g.items) rows.push({ kind: 'issue', issue: it, groupKey: g.key });
      }
    }
    return rows;
  }, [groupedRows, collapsed]);

  // Map issue id → row index for arrow drawing.
  const rowIndex = useMemo(() => {
    const m = new Map();
    flatRows.forEach((r, i) => { if (r.kind === 'issue') m.set(r.issue.id, i); });
    return m;
  }, [flatRows]);

  const todayX = layout ? xForDate(ymd(new Date())) : 0;

  function scrollToToday() {
    if (!scrollerRef.current || !layout) return;
    scrollerRef.current.scrollLeft = Math.max(0, todayX - 200);
  }

  // ── Drag (move + resize) ────────────────────────────────────────────
  const handleBarMouseDown = (issue, mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDrag({
      mode,
      issueId: issue.id,
      startX: e.clientX,
      originalStart: issue.scheduled_start,
      originalEnd: issue.scheduled_end || issue.scheduled_start,
      currentStart: issue.scheduled_start,
      currentEnd: issue.scheduled_end || issue.scheduled_start,
    });
  };

  useEffect(() => {
    if (!drag || !layout) return;
    const onMove = (e) => {
      const dx = e.clientX - drag.startX;
      const days = Math.round((dx / layout.colWidth) * layout.daysPerCol);
      let newStart = drag.originalStart, newEnd = drag.originalEnd;
      if (drag.mode === 'move') {
        newStart = ymd(addDays(parseYmd(drag.originalStart), days));
        newEnd = ymd(addDays(parseYmd(drag.originalEnd), days));
      } else if (drag.mode === 'resize-end') {
        newEnd = ymd(addDays(parseYmd(drag.originalEnd), days));
        if (newEnd < newStart) newEnd = newStart;
      } else if (drag.mode === 'resize-start') {
        newStart = ymd(addDays(parseYmd(drag.originalStart), days));
        if (newStart > newEnd) newStart = newEnd;
      }
      setDrag((d) => ({ ...d, currentStart: newStart, currentEnd: newEnd }));
    };
    const onUp = async () => {
      const { issueId, currentStart, currentEnd, originalStart, originalEnd } = drag;
      setDrag(null);
      if (currentStart === originalStart && currentEnd === originalEnd) return;
      // Optimistic update.
      setData((d) => ({
        ...d,
        issues: d.issues.map((i) => i.id === issueId
          ? { ...i, scheduled_start: currentStart, scheduled_end: currentEnd }
          : i),
      }));
      try {
        setSaving(true);
        await ganttApi.bulkUpdate(key, [{ id: issueId, scheduled_start: currentStart, scheduled_end: currentEnd }]);
      } catch {
        reload();
      } finally {
        setSaving(false);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, layout, reload, key]);

  // ── Actions: snapshot baseline, export ──────────────────────────────
  async function snapshotBaseline() {
    const name = window.prompt('Baseline name', `Snapshot ${ymd(new Date())}`);
    if (!name) return;
    try {
      await ganttApi.createBaseline(key, name);
      reload();
    } catch (e) {
      window.alert('Failed: ' + (e?.response?.data?.error?.message || e.message));
    }
  }

  function downloadCsv() {
    // Use api client base URL via a hidden anchor; the api client handles auth via interceptor.
    // For the simple download, we trigger the export endpoint which sends the file.
    import('../../api/client.js').then(({ api }) => {
      api.get(`/projects/${key}/gantt/export?format=csv`, { responseType: 'blob' })
        .then((res) => {
          const blob = new Blob([res.data], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${key}-gantt.csv`;
          a.click();
          URL.revokeObjectURL(url);
        });
    });
  }

  function toggleGroup(gKey) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(gKey)) next.delete(gKey); else next.add(gKey);
      return next;
    });
  }

  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(groupedRows.filter((g) => g.label !== null).map((g) => g.key)));

  if (!data) return <div className="muted" style={{ padding: 24 }}>Loading timeline…</div>;

  const criticalSet = new Set(criticalPath?.all_critical_ids || []);
  const baselineMap = new Map((activeBaseline?.snapshot || []).map((b) => [b.issue_id, b]));

  // ── Render header column lines + labels ─────────────────────────────
  function renderHeader() {
    const cells = [];
    let lastMonth = '';
    for (let i = 0; i <= layout.cols; i++) {
      const colDate = addDays(layout.min, i * layout.daysPerCol);
      const x = i * layout.colWidth;
      cells.push(
        <line key={'l' + i} x1={x} x2={x} y1={0}
              y2={HEADER_HEIGHT + MILESTONE_BAND + flatRows.length * (ROW_HEIGHT + ROW_GAP)}
              stroke="#e2e8f0" strokeWidth={1} />
      );
      if (i < layout.cols) {
        const ml = `${shortMonth(colDate)} ${colDate.getUTCFullYear()}`;
        if (ml !== lastMonth) {
          lastMonth = ml;
          cells.push(<text key={'m' + i} x={x + 4} y={16} fontSize={11} fontWeight={600} fill="#0f172a">{ml}</text>);
        }
        let label = '';
        if (zoom === 'week') label = `${dayLabel(colDate)} ${colDate.getUTCDate()}`;
        else if (zoom === 'month') label = `W${Math.ceil(colDate.getUTCDate() / 7)}`;
        else label = shortMonth(colDate);
        cells.push(<text key={'h' + i} x={x + 4} y={HEADER_HEIGHT - 8} fontSize={10} fill="#475569">{label}</text>);
      }
    }
    return cells;
  }

  // ── Issue bar geometry ──────────────────────────────────────────────
  function rowYForIssue(idx) {
    return HEADER_HEIGHT + MILESTONE_BAND + idx * (ROW_HEIGHT + ROW_GAP) + ROW_GAP / 2;
  }

  // ── Empty state ─────────────────────────────────────────────────────
  if (data.issues.length === 0 && data.unscheduled.length === 0) {
    return (
      <section style={{ padding: 24 }}>
        <h2>Timeline</h2>
        <div className="card" style={{ padding: 24, textAlign: 'center' }}>
          <p className="muted">No issues yet — create some on the Backlog or Board, then schedule them here.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="timeline-view" data-testid="gantt-timeline">
      <header className="page-header" style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0 }}>Timeline</h2>
        <div style={{ flex: 1 }} />
        <select aria-label="Zoom" value={zoom} onChange={(e) => setZoom(e.target.value)} data-testid="gantt-zoom">
          {Object.entries(ZOOM_LEVELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select aria-label="Group by" value={groupBy} onChange={(e) => setGroupBy(e.target.value)} data-testid="gantt-group">
          <option value="none">No grouping</option>
          <option value="assignee">By assignee</option>
          <option value="epic">By epic</option>
          <option value="status">By status</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
          <input type="checkbox" checked={showDeps} onChange={(e) => setShowDeps(e.target.checked)} />
          Dependencies
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
          <input type="checkbox" checked={showCritical} onChange={(e) => setShowCritical(e.target.checked)} data-testid="toggle-critical" />
          Critical path
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
          <input type="checkbox" checked={showBaseline} onChange={(e) => setShowBaseline(e.target.checked)} disabled={!data.baselines?.length} data-testid="toggle-baseline" />
          Baseline
        </label>
        {groupBy !== 'none' && (
          <>
            <button className="btn ghost small" onClick={expandAll} data-testid="gantt-expand-all">Expand all</button>
            <button className="btn ghost small" onClick={collapseAll} data-testid="gantt-collapse-all">Collapse all</button>
          </>
        )}
        <button className="btn ghost small" onClick={scrollToToday}>Today</button>
        <button className="btn ghost small" onClick={snapshotBaseline} data-testid="snapshot-baseline">Snapshot</button>
        <button className="btn ghost small" onClick={downloadCsv} data-testid="export-csv">Export CSV</button>
        {saving && <span className="muted small" aria-live="polite">Saving…</span>}
      </header>

      {/* One scroll container for sidebar + chart so the two always grow, shrink
          and scroll together as groups are expanded/collapsed. The task list is
          pinned to the left edge while the chart scrolls horizontally beneath it. */}
      <div ref={scrollerRef} data-testid="gantt-scroller" style={{ width: 0, minWidth: '100%', overflow: 'auto', maxHeight: 'calc(100vh - 180px)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', width: 'max-content', minWidth: '100%' }}>
        {/* Sidebar: task list */}
        <aside style={{ position: 'sticky', left: 0, zIndex: 2, flex: `0 0 ${SIDEBAR_WIDTH}px`, width: SIDEBAR_WIDTH, borderRight: '1px solid #e2e8f0', background: '#f8fafc', overflow: 'hidden' }}>
          <div style={{ height: HEADER_HEIGHT, borderBottom: '1px solid #e2e8f0', padding: '0 12px', display: 'flex', alignItems: 'flex-end', paddingBottom: 8, fontWeight: 600, fontSize: 12, color: '#475569', textTransform: 'uppercase', letterSpacing: '.05em' }}>
            Tasks ({data.issues.length})
          </div>
          <div style={{ height: MILESTONE_BAND, borderBottom: '1px solid #f1f5f9' }} />
          {flatRows.map((row, idx) => {
            if (row.kind === 'group') {
              return (
                <div
                  key={'g' + row.group.key}
                  data-testid={`gantt-group-${row.group.key}`}
                  onClick={() => toggleGroup(row.group.key)}
                  style={{ height: ROW_HEIGHT + ROW_GAP, padding: '0 12px', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', background: '#eef2f6', fontSize: 12, fontWeight: 600, color: '#334155' }}
                >
                  <span style={{ width: 10, display: 'inline-block' }}>{collapsed.has(row.group.key) ? '▶' : '▼'}</span>
                  <span>{row.group.label}</span>
                  <span className="muted small" style={{ marginLeft: 'auto' }}>{row.group.items.length}</span>
                </div>
              );
            }
            const i = row.issue;
            const color = i.gantt_color || FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
            const isCritical = criticalSet.has(i.id);
            return (
              <div
                key={i.id}
                data-testid={`gantt-row-${i.id}`}
                style={{
                  height: ROW_HEIGHT + ROW_GAP,
                  padding: '0 12px',
                  borderBottom: '1px solid #f1f5f9',
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 12,
                }}
                title={`${i.full_key} — ${i.title}`}
              >
                <span style={{ width: 6, height: 6, background: color, borderRadius: 3, flexShrink: 0 }} />
                <span style={{ fontFamily: 'monospace', color: '#64748b', fontSize: 10 }}>{i.full_key}</span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: isCritical ? 600 : 400 }}>{i.title}</span>
                {i.assignee_name && <span className="muted small" style={{ fontSize: 10 }}>{i.assignee_name.split(' ')[0]}</span>}
              </div>
            );
          })}
        </aside>

        {/* SVG canvas */}
        <div style={{ flex: '0 0 auto' }}>
          {layout && (
            <svg
              data-testid="gantt-svg"
              width={layout.width}
              height={HEADER_HEIGHT + MILESTONE_BAND + flatRows.length * (ROW_HEIGHT + ROW_GAP) + 16}
              style={{ display: 'block' }}
            >
              <rect x={0} y={0} width={layout.width} height={HEADER_HEIGHT} fill="#f8fafc" />
              {renderHeader()}
              <line x1={0} y1={HEADER_HEIGHT} x2={layout.width} y2={HEADER_HEIGHT} stroke="#cbd5e1" />

              {/* Today line */}
              {todayX >= 0 && todayX <= layout.width && (
                <line
                  data-testid="today-line"
                  x1={todayX} x2={todayX} y1={0}
                  y2={HEADER_HEIGHT + MILESTONE_BAND + flatRows.length * (ROW_HEIGHT + ROW_GAP)}
                  stroke="#dc2626" strokeWidth={1.5} strokeDasharray="4 3"
                />
              )}

              {/* Milestone diamonds (sprint end + version release) */}
              {data.milestones?.map((m) => {
                const x = xForDate(m.date);
                const y = HEADER_HEIGHT + MILESTONE_BAND / 2;
                const fill = m.kind === 'version' ? '#9333ea' : '#0ea5e9';
                return (
                  <g key={`${m.kind}-${m.id}`}
                     onMouseEnter={(e) => setHover({ x: e.clientX, y: e.clientY, content: `${m.name}\n${m.kind} · ${m.date}` })}
                     onMouseLeave={() => setHover(null)}>
                    <polygon points={`${x},${y - 6} ${x + 6},${y} ${x},${y + 6} ${x - 6},${y}`} fill={fill} />
                  </g>
                );
              })}

              {/* Baseline ghost bars */}
              {showBaseline && activeBaseline?.snapshot && flatRows.map((row, idx) => {
                if (row.kind !== 'issue') return null;
                const b = baselineMap.get(row.issue.id);
                if (!b?.scheduled_start) return null;
                const sx = xForDate(b.scheduled_start);
                const ex = xForDate(b.scheduled_end || b.scheduled_start);
                const w = Math.max(4, ex - sx + layout.colWidth / layout.daysPerCol);
                const y = rowYForIssue(idx) + ROW_HEIGHT - 6;
                return (
                  <rect key={'b' + row.issue.id}
                        x={sx} y={y} width={w} height={4} rx={2}
                        fill="#94a3b8" fillOpacity={0.6}
                        data-testid={`gantt-baseline-${row.issue.id}`} />
                );
              })}

              {/* Dependency arrows */}
              {showDeps && data.dependencies?.map((dep) => {
                const fromIdx = rowIndex.get(dep.predecessor_id);
                const toIdx = rowIndex.get(dep.successor_id);
                if (fromIdx == null || toIdx == null) return null;
                const fromIssue = flatRows[fromIdx].issue;
                const toIssue = flatRows[toIdx].issue;
                if (!fromIssue.scheduled_end || !toIssue.scheduled_start) return null;
                const x1 = xForDate(fromIssue.scheduled_end) + layout.colWidth / layout.daysPerCol;
                const y1 = rowYForIssue(fromIdx) + ROW_HEIGHT / 2;
                const x2 = xForDate(toIssue.scheduled_start);
                const y2 = rowYForIssue(toIdx) + ROW_HEIGHT / 2;
                // Elbow path: right, down/up, right.
                const midX = x1 + Math.max(8, (x2 - x1) / 2);
                const path = `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2 - 4} ${y2}`;
                return (
                  <g key={dep.id} data-testid={`gantt-dep-${dep.id}`}>
                    <path d={path} fill="none" stroke="#475569" strokeWidth={1.2} />
                    <polygon points={`${x2},${y2} ${x2 - 5},${y2 - 3} ${x2 - 5},${y2 + 3}`} fill="#475569" />
                  </g>
                );
              })}

              {/* Issue bars */}
              {flatRows.map((row, idx) => {
                if (row.kind !== 'issue') return null;
                const i = row.issue;
                const live = drag && drag.issueId === i.id
                  ? { start: drag.currentStart, end: drag.currentEnd }
                  : { start: i.scheduled_start, end: i.scheduled_end || i.scheduled_start };
                const sx = xForDate(live.start);
                const ex = xForDate(live.end);
                const w = Math.max(8, ex - sx + layout.colWidth / layout.daysPerCol);
                const y = rowYForIssue(idx);
                const color = i.gantt_color || FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
                const isCritical = criticalSet.has(i.id);
                const isDone = i.status === 'done' || i.status === 'closed';
                return (
                  <g key={i.id}
                     data-testid={`gantt-bar-${i.id}`}
                     onMouseEnter={(e) => setHover({
                       x: e.clientX, y: e.clientY,
                       content: `${i.full_key} ${i.title}\n${live.start} → ${live.end}\n${i.estimated_hours ?? '—'}h · ${i.status}`,
                     })}
                     onMouseLeave={() => setHover(null)}>
                    <rect
                      x={sx} y={y} width={w} height={ROW_HEIGHT - 4} rx={3}
                      fill={color} fillOpacity={isDone ? 0.45 : 0.85}
                      stroke={isCritical ? '#dc2626' : color}
                      strokeWidth={isCritical ? 2 : 1}
                      style={{ cursor: 'grab' }}
                      onMouseDown={handleBarMouseDown(i, 'move')}
                    />
                    {w > 60 && (
                      <text x={sx + 6} y={y + (ROW_HEIGHT - 4) / 2 + 4}
                            fontSize={11} fill="#fff" fontWeight={500}
                            style={{ pointerEvents: 'none' }}>
                        {i.title.length > Math.floor(w / 6) ? i.title.slice(0, Math.floor(w / 6) - 1) + '…' : i.title}
                      </text>
                    )}
                    {/* Resize handles */}
                    <rect x={sx - 3} y={y} width={6} height={ROW_HEIGHT - 4}
                          fill="transparent" style={{ cursor: 'col-resize' }}
                          onMouseDown={handleBarMouseDown(i, 'resize-start')}
                          data-testid={`gantt-resize-start-${i.id}`} />
                    <rect x={sx + w - 3} y={y} width={6} height={ROW_HEIGHT - 4}
                          fill="transparent" style={{ cursor: 'col-resize' }}
                          onMouseDown={handleBarMouseDown(i, 'resize-end')}
                          data-testid={`gantt-resize-end-${i.id}`} />
                  </g>
                );
              })}
            </svg>
          )}
        </div>
      </div>
      </div>

      {/* Unscheduled list */}
      {data.unscheduled?.length > 0 && (
        <aside data-testid="gantt-unscheduled" style={{ borderTop: '1px solid #e2e8f0', padding: '12px 20px', maxHeight: 200, overflowY: 'auto', background: '#fafbfc' }}>
          <div style={{ fontWeight: 600, fontSize: 12, color: '#475569', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
            Unscheduled ({data.unscheduled.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {data.unscheduled.slice(0, 50).map((i) => (
              <span key={i.id} className="chip" style={{ fontSize: 11 }}>
                {i.full_key} {i.title}
              </span>
            ))}
            {data.unscheduled.length > 50 && <span className="muted small">+{data.unscheduled.length - 50} more</span>}
          </div>
        </aside>
      )}

      {hover && (
        <div style={{
          position: 'fixed', left: hover.x + 12, top: hover.y + 12,
          background: '#0f172a', color: '#fff', padding: '6px 10px',
          borderRadius: 4, fontSize: 11, whiteSpace: 'pre',
          pointerEvents: 'none', zIndex: 1000,
        }}>{hover.content}</div>
      )}
    </section>
  );
}
